/**
 * useCandidateDetails Composable
 *
 * Fetches detailed contract information for a specific candidate using the
 * existing getContractDetails API endpoint. This composable is used in the
 * single candidate view to show comprehensive candidate information.
 */
import { computed, type MaybeRefOrGetter } from 'vue';
import { toValue } from 'vue';
import { useGetContractDetails } from '@/api/forecasttrader.queries';
import type { ForecastTraderContractDetailsParams } from '@/types/forecasttrader.types';

export interface UseCandidateDetailsReturn {
  /** Contract details data from API */
  candidateDetails: ReturnType<typeof useGetContractDetails>['data'];
  /** Loading state for contract details */
  isLoadingDetails: ReturnType<typeof useGetContractDetails>['isLoading'];
  /** Error state for contract details */
  detailsError: ReturnType<typeof useGetContractDetails>['error'];
  /** Whether the query is currently fetching */
  isFetchingDetails: ReturnType<typeof useGetContractDetails>['isFetching'];
}

/**
 * Composable for fetching individual candidate contract details
 *
 * @param conid - The contract ID for the candidate
 * @returns Contract details with loading and error states
 *
 * @example
 * ```vue
 * <script setup>
 * const { candidateDetails, isLoadingDetails, detailsError } = useCandidateDetails(123456)
 * </script>
 * ```
 */
export function useCandidateDetails(conid: MaybeRefOrGetter<number | null | undefined>): UseCandidateDetailsReturn {
  // Create reactive params for the contract details query
  const contractDetailsParams = computed<ForecastTraderContractDetailsParams>(() => ({
    conid: toValue(conid) || 0,
  }));

  // Use existing contract details query hook
  const contractDetailsQuery = useGetContractDetails(contractDetailsParams);

  return {
    candidateDetails: contractDetailsQuery.data,
    isLoadingDetails: contractDetailsQuery.isLoading,
    detailsError: contractDetailsQuery.error,
    isFetchingDetails: contractDetailsQuery.isFetching,
  };
}
